import React, { useState } from 'react';

import { API } from 'constants';
import client from '../../../../services/library-api';

import { useFormState } from '../../../../hooks/useFormState';

import { urlRegex } from '../../../../constants/regex_patterns';
import { defaultErrorMessage, emptyFieldErrorMessage } from '../../../../constants/errorMessages';

import Modal from '../../../../design-system/Modal/Modal';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import ModalHeader from '../../../../design-system/ModalHeader/ModalHeader';
import { Button, Input } from '../../../../design-system';

const WebhookNotificationModal = ({
    agentId,
    mode: _mode,
    data,
    setWebhookNotifications,
    updateNotificationInState,
    onClose,
}) => {
    const mode = _mode === 'edit' ? 'edit' : 'create';

    const { formData, handleInputChange, fieldErrorMessages, setFieldErrorMessages } = useFormState(
        () => {
            if (mode === 'edit' && data) {
                return {
                    name: data.name,
                    webhook_url: data.detail?.webhook_url,
                };
            }
            return { name: '', webhook_url: '' };
        }
    );

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const handleAdd = async () => {
        const requestBody = {
            name: formData.name,
            event_listener: 'on_thread_success',
            delivery: 'webhook',
            is_live: true,
            detail: { webhook_url: formData.webhook_url },
        };
        const { data } = await client.post(
            API.ROUTES.library.process + agentId + '/event/',
            requestBody
        );
        setWebhookNotifications((notifications) => [...(notifications || []), data]);
    };

    const handleEdit = async () => {
        const { id, event_listener, delivery, is_live } = data;
        const requestBody = {
            name: formData.name,
            event_listener,
            delivery,
            is_live,
            detail: { ...data.detail, webhook_url: formData.webhook_url },
        };
        const { data: response } = await client.patch(
            API.ROUTES.library.process + agentId + '/event/' + id + '/',
            requestBody
        );
        updateNotificationInState(response);
    };

    const handleAction = async () => {
        try {
            const areEmptyFields = Object.values(formData)?.some((value) => !value);
            if (areEmptyFields) {
                setFieldErrorMessages({
                    name: formData.name ? null : emptyFieldErrorMessage,
                    webhook_url: formData.webhook_url ? null : emptyFieldErrorMessage,
                });
                return;
            }

            const isUrlValid = urlRegex.test(formData.webhook_url?.trim());
            if (!isUrlValid) {
                setFieldErrorMessages({ webhook_url: 'Please enter a valid webhook URL.' });
                return;
            }

            setIsLoading(true);

            const action = {
                create: handleAdd,
                edit: handleEdit,
            }[mode];

            await action();

            onClose();
        } catch (e) {
            setIsLoading(false);
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        }
    };

    const title = {
        create: 'Add Webhook',
        edit: 'Edit Webhook',
    }[mode];

    const submitButtonText = {
        create: 'Add',
        edit: 'Save',
    }[mode];

    return (
        <Modal size="medium" resetPadding onClose={onClose}>
            <ModalHeader title={title} onClose={onClose} outlined />

            <div className="px-5 sm:px-6 md:px-8 py-3 md:py-5 flex flex-col gap-4">
                <Input
                    size="sm"
                    name="notification_name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    label="Notification Name"
                    isRequired
                    placeholder="Give your notification a unique name to help identify it"
                    state={fieldErrorMessages.name ? 'error' : 'default'}
                    errorMessage={fieldErrorMessages.name}
                />

                <Input
                    size="sm"
                    name="webhook_url"
                    value={formData.webhook_url}
                    onChange={(e) => handleInputChange('webhook_url', e.target.value)}
                    label="Webhook URL"
                    isRequired
                    placeholder="Enter a valid webhook URL"
                    state={fieldErrorMessages.webhook_url ? 'error' : 'default'}
                    errorMessage={fieldErrorMessages.webhook_url}
                />

                <div className="flex items-center justify-between gap-2">
                    <Button size="md" type="neutral" text="Cancel" onClick={onClose} />
                    <Button
                        size="md"
                        type="secondary"
                        text={submitButtonText}
                        state={isLoading ? 'loading' : 'default'}
                        onClick={handleAction}
                    />
                </div>
            </div>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </Modal>
    );
};

export default WebhookNotificationModal;
